import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Search, Filter, Package, ChevronDown } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const BRANDS_PER_LOAD = 10;

const Catalog = () => {
  const [searchParams] = useSearchParams();
  const [allProducts, setAllProducts] = useState([]);
  const [brands, setBrands] = useState([]);
  const [searchTerm, setSearchTerm] = useState(searchParams.get('search') || '');
  const [selectedBrand, setSelectedBrand] = useState(searchParams.get('brand') || '');
  const [loading, setLoading] = useState(false);
  const [visibleBrandsCount, setVisibleBrandsCount] = useState(BRANDS_PER_LOAD);

  useEffect(() => {
    fetchBrands();
    fetchAllProducts();
  }, []);

  const fetchBrands = async () => {
    try {
      const response = await axios.get(`${API}/products/brands`);
      setBrands(response.data.brands);
    } catch (error) {
      console.error('Error fetching brands:', error);
    }
  };

  const fetchAllProducts = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/products`, { params: { limit: 5000 } });
      setAllProducts(response.data);
    } catch (error) {
      console.error('Error fetching products:', error);
    } finally {
      setLoading(false);
    }
  };

  // Memoized filtered products
  const filteredProducts = useMemo(() => {
    let filtered = [...allProducts];

    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter(
        (p) =>
          p.brand.toLowerCase().includes(searchLower) ||
          p.product_name.toLowerCase().includes(searchLower)
      );
    }

    if (selectedBrand && selectedBrand !== 'all') {
      filtered = filtered.filter((p) => p.brand === selectedBrand);
    }

    return filtered;
  }, [allProducts, searchTerm, selectedBrand]);

  // Memoized grouped products
  const groupedProducts = useMemo(() => {
    const grouped = {};
    filteredProducts.forEach((product) => {
      if (!grouped[product.brand]) {
        grouped[product.brand] = [];
      }
      grouped[product.brand].push(product);
    });
    return grouped;
  }, [filteredProducts]);

  // Visible brands based on lazy loading
  const visibleBrands = useMemo(() => {
    const sortedBrands = Object.keys(groupedProducts).sort();
    return sortedBrands.slice(0, visibleBrandsCount);
  }, [groupedProducts, visibleBrandsCount]);

  const handleLoadMore = useCallback(() => {
    setVisibleBrandsCount((prev) => prev + BRANDS_PER_LOAD);
  }, []);

  const handleSearch = useCallback(() => {
    setVisibleBrandsCount(BRANDS_PER_LOAD);
  }, []);

  const handleBrandChange = useCallback((value) => {
    setSelectedBrand(value);
    setVisibleBrandsCount(BRANDS_PER_LOAD);
  }, []);

  const clearFilters = useCallback(() => {
    setSearchTerm('');
    setSelectedBrand('');
    setVisibleBrandsCount(BRANDS_PER_LOAD);
  }, []);

  const totalBrands = Object.keys(groupedProducts).length;
  const hasMoreBrands = visibleBrandsCount < totalBrands;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Navigation */}
      <nav className="fixed top-0 w-full bg-white shadow-md z-50 backdrop-blur-sm bg-opacity-95">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-20">
            <div className="flex items-center space-x-3 cursor-pointer" onClick={() => window.location.href = '/'}>
              <img src="/saifoodhouse-logo.png" alt="Sai Food House" className="h-14" />
            </div>
            <div className="hidden md:flex space-x-8">
              <a href="/" className="text-gray-700 hover:text-[#fcce32] font-semibold transition-colors" data-testid="nav-home">Home</a>
              <a href="/catalog" className="text-[#0b1f3f] hover:text-[#fcce32] font-semibold transition-colors" data-testid="nav-catalog">Products</a>
              <a href="/about" className="text-gray-700 hover:text-[#fcce32] font-semibold transition-colors" data-testid="nav-about">About Us</a>
              <a href="/contact" className="text-gray-700 hover:text-[#fcce32] font-semibold transition-colors" data-testid="nav-contact">Contact</a>
            </div>
          </div>
        </div>
      </nav>

      {/* Brand Showcase Banner */}
      <section className="pt-20 bg-gradient-to-r from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="rounded-2xl overflow-hidden shadow-lg">
            <img src="/culinary-ribbon.png" alt="Premium Brands" className="w-full h-auto" loading="lazy" />
          </div>
        </div>
      </section>

      {/* Catalog Content */}
      <div className="pb-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Page Header */}
          <div className="text-center mb-12">
            <h1 className="text-4xl sm:text-5xl font-bold text-[#0b1f3f] mb-4" data-testid="catalog-heading">
              Complete Product Catalog
            </h1>
            <p className="text-lg text-gray-600">
              {loading ? 'Loading...' : `Explore all ${filteredProducts.length} products from ${totalBrands} brands`}
            </p>
          </div>

          {/* Search and Filter */}
          <div className="bg-white rounded-2xl shadow-lg p-6 mb-12 sticky top-24 z-40">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="md:col-span-2 relative">
                <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <Input
                  type="text"
                  placeholder="Search by brand or product name..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                  className="pl-12 h-12 text-base"
                  data-testid="catalog-search-input"
                />
              </div>
              <div className="flex gap-2">
                <div className="flex-1 relative">
                  <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4 z-10" />
                  <Select value={selectedBrand} onValueChange={handleBrandChange}>
                    <SelectTrigger className="h-12 pl-10" data-testid="brand-filter">
                      <SelectValue placeholder="All Brands" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Brands</SelectItem>
                      {brands.map((brand) => (
                        <SelectItem key={brand} value={brand}>
                          {brand}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                {(searchTerm || selectedBrand) && (
                  <Button
                    onClick={clearFilters}
                    variant="outline"
                    className="h-12 px-6"
                  >
                    Clear
                  </Button>
                )}
              </div>
            </div>
          </div>

          {/* Products Grouped by Brand */}
          {loading ? (
            <div className="text-center py-20">
              <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-[#fcce32] mx-auto"></div>
              <p className="mt-4 text-gray-600">Loading products...</p>
            </div>
          ) : filteredProducts.length === 0 ? (
            <div className="text-center py-20">
              <Package className="w-20 h-20 text-gray-400 mx-auto mb-4" />
              <p className="text-2xl text-gray-600">No products found</p>
              <p className="text-gray-500 mt-2">Try adjusting your search or filter</p>
              <Button onClick={clearFilters} className="mt-6 bg-[#fcce32] text-[#0b1f3f] hover:bg-[#e0b82a]">
                Clear Filters
              </Button>
            </div>
          ) : (
            <>
              <div className="space-y-12">
                {visibleBrands.map((brand) => (
                  <BrandSection
                    key={brand}
                    brand={brand}
                    products={groupedProducts[brand]}
                  />
                ))}
              </div>

              {/* Load More Button */}
              {hasMoreBrands && (
                <div className="text-center mt-12">
                  <Button
                    onClick={handleLoadMore}
                    className="h-14 px-10 bg-[#0b1f3f] text-white hover:bg-[#1a3a5c] font-semibold rounded-xl"
                  >
                    Load More Brands ({totalBrands - visibleBrandsCount} remaining)
                    <ChevronDown className="ml-2 w-5 h-5" />
                  </Button>
                </div>
              )}

              {/* Total Summary */}
              {!hasMoreBrands && (
                <div className="mt-12 bg-gradient-to-r from-[#0b1f3f] to-[#1a3a5c] rounded-2xl shadow-xl p-8 text-center text-white">
                  <h3 className="text-3xl font-bold mb-2">Complete Catalog</h3>
                  <p className="text-xl">
                    Showing <span className="text-[#fcce32] font-bold">{filteredProducts.length}</span> products 
                    from <span className="text-[#fcce32] font-bold">{totalBrands}</span> brands
                  </p>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-[#0b1f3f] text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-8">
            <div>
              <img src="/saifoodhouse-logo.png" alt="Sai Food House" className="h-12 mb-4" />
              <p className="text-gray-300">
                Your trusted distributor of quality food & beverage products across India.
              </p>
            </div>
            <div>
              <h4 className="text-lg font-semibold mb-4 text-[#fcce32]">Contact Info</h4>
              <p className="text-gray-300 mb-2">82, Chauhan Market Rd, C Block</p>
              <p className="text-gray-300 mb-2">Sector 53, Noida, UP 201307</p>
              <p className="text-gray-300">Phone: 085888 24881</p>
            </div>
            <div>
              <h4 className="text-lg font-semibold mb-4 text-[#fcce32]">Quick Links</h4>
              <div className="space-y-2">
                <a href="/catalog" className="block text-gray-300 hover:text-[#fcce32] transition-colors">Products</a>
                <a href="/about" className="block text-gray-300 hover:text-[#fcce32] transition-colors">About Us</a>
                <a href="/contact" className="block text-gray-300 hover:text-[#fcce32] transition-colors">Contact Us</a>
              </div>
            </div>
          </div>
          <div className="border-t border-gray-700 pt-8 text-center text-gray-400">
            <p>© 2025 Sai Food House. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

// Memoized Brand Section Component
const BrandSection = React.memo(({ brand, products }) => {
  return (
    <div className="bg-white rounded-2xl shadow-lg p-8" data-testid={`brand-section-${brand}`}>
      {/* Brand Header */}
      <div className="flex items-center justify-between mb-6 pb-4 border-b-2 border-[#fcce32]">
        <div className="flex items-center space-x-4">
          <div className="w-16 h-16 bg-[#0b1f3f] rounded-xl flex items-center justify-center">
            <span className="text-3xl font-bold text-[#fcce32]">{brand.charAt(0)}</span>
          </div>
          <div>
            <h2 className="text-3xl font-bold text-[#0b1f3f]">{brand}</h2>
            <p className="text-gray-600 mt-1">{products.length} Products</p>
          </div>
        </div>
      </div>

      {/* Products Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        {products.map((product) => (
          <ProductCard key={product.id} product={product} />
        ))}
      </div>
    </div>
  );
});

// Memoized Product Card Component
const ProductCard = React.memo(({ product }) => {
  return (
    <div className="bg-gradient-to-br from-gray-50 to-white rounded-xl shadow-sm hover:shadow-md transition-all p-4 border border-gray-100">
      <div className="flex items-start space-x-3">
        <div className="flex-shrink-0 w-10 h-10 bg-[#fcce32] bg-opacity-10 rounded-lg flex items-center justify-center">
          <Package className="w-5 h-5 text-[#fcce32]" />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="font-semibold text-gray-800 text-sm leading-tight mb-1">
            {product.product_name}
          </h3>
          <p className="text-xs text-gray-600 font-medium">{product.weight_pack}</p>
        </div>
      </div>
    </div>
  );
});

BrandSection.displayName = 'BrandSection';
ProductCard.displayName = 'ProductCard';

export default Catalog;
